import { useAtomValue } from 'jotai'
import { motion } from 'motion/react'
import { Fragment, useMemo } from 'react'
import { Jobibot, StatefulImage } from '../../rapid-ui'
import { DefaultAttribution } from '../simulator-tools/attribution'
import { campaignDetailsBrandingAtom } from '../state'

const PADDING = 36 // Default globalPadding value

function PlaceholderText({ text }: { text: string }) {
	return (
		<div>
			{text.split('').map((char, index) => (
				<Fragment key={index}>{char === ' ' ? <>&#32;</> : <>&#x25AE;</>}</Fragment>
			))}
		</div>
	)
}

export function MockFeedAd() {
	// Generate a random animation delay between 0 and -45 seconds (negative to start at different points)
	const animationDelay = useMemo(() => `${-Math.random() * 45}s`, [])

	// Generate stable random delays for the four floating blur circles
	const floatDelays = useMemo(() => ({
		float1: -Math.random() * 20,
		float2: -Math.random() * 25,
		float3: -Math.random() * 30,
		float4: -Math.random() * 18,
	}), [])

	const activeBranding = useAtomValue(campaignDetailsBrandingAtom)
	const accentForeground = activeBranding?.accentForeground ?? '#FFFFFF'
	const accentBackground = activeBranding?.accentBackground ?? '#000000'

	return (
		<div className="relative z-10 select-none">
			<motion.div
				initial={{
					scale: 0.98,
					translateY: 10,
					opacity: 0,
				}}
				animate={{
					scale: 1,
					translateY: 0,
					opacity: 1,
				}}
				transition={{
					duration: 0.3,
					translateY: { 'duration': 0.9, 'bounce': 0.3, type: 'spring' },
				}}
				className="ring perspective-dramatic ring-grey-200 bg-white flex flex-col w-[500px] rounded-sm p-4 shadow-lg"
			>
				{/* Header - Facebook style */}
				<div
					className="flex items-center gap-2"
					style={{
						fontFamily: 'Segoe UI Historic, Segoe UI, Helvetica, Arial, sans-serif',
					}}
				>
					<div className="h-12 w-12 flex items-center justify-center">
						<Jobibot size={40} />
					</div>
					<div className="">
						<div className="font-medium text-grey-900">Jobilla</div>
						<div className="text-grey-600 text-sm">Sponsored</div>
					</div>
				</div>

				{/* Primary text */}
				<div
					className="text-sm mt-4 text-grey-900"
					style={{
						fontFamily: 'Segoe UI Historic, Segoe UI, Helvetica, Arial, sans-serif',
					}}
				>
					<PlaceholderText text="We're hiring! Join our team and make an impact." />
				</div>

				{/* Image area with content overlay - mimics vertical stack orientation */}
				<div
					className="aspect-[4/5] mt-4 relative transition-all overflow-hidden"
					style={{
						backgroundColor: '#000000',
						color: 'white',
					}}
				>
					{/* Animated gradient background */}
					<div
						className="absolute inset-0"
						style={{
							background: 'linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)',
							backgroundSize: '400% 400%',
							animation: 'gradient 45s ease infinite',
							animationDelay,
						}}
					/>

					{/* Blurred shapes layer with blend mode */}
					<div className="absolute inset-0" style={{ mixBlendMode: 'overlay' }}>
						{/* Large blur circle 1 */}
						<div
							className="absolute rounded-full"
							style={{
								width: '300px',
								height: '300px',
								background:
									'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
								filter: 'blur(60px)',
								top: '-50px',
								left: '-50px',
								animation: 'float1 20s ease-in-out infinite',
								animationDelay: `${floatDelays.float1}s`,
							}}
						/>
						{/* Large blur circle 2 */}
						<div
							className="absolute rounded-full"
							style={{
								width: '250px',
								height: '250px',
								background:
									'radial-gradient(circle, rgba(255,200,255,0.6) 0%, rgba(255,200,255,0) 70%)',
								filter: 'blur(50px)',
								bottom: '-80px',
								right: '-60px',
								animation: 'float2 25s ease-in-out infinite',
								animationDelay: `${floatDelays.float2}s`,
							}}
						/>
						{/* Medium blur circle 3 */}
						<div
							className="absolute rounded-full"
							style={{
								width: '200px',
								height: '200px',
								background:
									'radial-gradient(circle, rgba(150,200,255,0.7) 0%, rgba(150,200,255,0) 70%)',
								filter: 'blur(40px)',
								top: '50%',
								right: '10%',
								animation: 'float3 30s ease-in-out infinite',
								animationDelay: `${floatDelays.float3}s`,
							}}
						/>
						{/* Small accent blur */}
						<div
							className="absolute rounded-full"
							style={{
								width: '150px',
								height: '150px',
								background:
									'radial-gradient(circle, rgba(255,220,150,0.5) 0%, rgba(255,220,150,0) 70%)',
								filter: 'blur(30px)',
								top: '20%',
								left: '60%',
								animation: 'float4 18s ease-in-out infinite',
								animationDelay: `${floatDelays.float4}s`,
							}}
						/>
					</div>

					{/* Content layer - matches feed-ad-content-layer vertical layout */}
					<div
						className="absolute inset-0 flex flex-col"
						style={{
							paddingTop: `${PADDING}px`,
						}}
					>
						{/* Top area with logo (top-left) and location (top-right) */}
						<div
							className="flex"
							style={{
								paddingLeft: `${PADDING}px`,
								paddingRight: `${PADDING}px`,
							}}
						>
							{/* Top left zone - Logo */}
							<div className="flex items-center flex-col gap-2">
								<div className="bg-white rounded-xl size-24 p-2 flex items-center justify-center">
									<StatefulImage
										src={activeBranding?.logo?.url ?? ''}
										alt="Logo"
										className="h-full w-full"
										objectFit="contain"
									/>
								</div>
							</div>

							{/* Spacer */}
							<div className="grow" />

							{/* Top right zone - Location */}
							<div className="flex flex-col gap-2 items-center text-sm">
								<PlaceholderText text="City, Country" />
							</div>
						</div>

						<div className="grow" />

						{/* Bottom left zone - Title and Subtitle */}
						<div
							className="relative flex justify-end flex-col gap-2 z-50"
							style={{
								paddingTop: `${Math.floor(PADDING / 1.5)}px`,
								paddingLeft: `${PADDING}px`,
								paddingRight: `${PADDING}px`,
							}}
						>
							<div className="font-bold text-3xl text-white">
								<PlaceholderText text="Senior Software Engineer" />
							</div>
							<div className="text-lg text-white/90">
								<PlaceholderText text="Be the wizards behind the curtain." />
							</div>
						</div>

						{/* Bottom area with CTA (bottom-left) and Attribution (bottom-right) */}
						<div
							className="flex justify-between items-end pt-6"
							style={{
								paddingLeft: `${PADDING}px`,
								paddingRight: `${PADDING}px`,
								paddingBottom: `${PADDING}px`,
							}}
						>
							<div
								className="py-2 px-4 rounded w-[max-content] transition-all"
								style={{
									backgroundColor: accentBackground,
									color: accentForeground,
								}}
							>
								<PlaceholderText text="Apply Now" />
							</div>

							{/* Attribution - bottom right */}
							<div className="flex flex-col gap-2 items-end">
								<DefaultAttribution />
							</div>
						</div>
					</div>
				</div>

				{/* Bottom meta section */}
				<div
					className="grid grid-cols-[1fr_auto] gap-4 p-3 items-center bg-grey-100"
					style={{
						fontFamily: 'Segoe UI Historic, Segoe UI, Helvetica, Arial, sans-serif',
					}}
				>
					<div className="flex flex-col min-w-0">
						<div className="text-grey-600 text-sm">FORMS.JOBILLA.COM</div>
						<div className="font-medium text-grey-900">
							<PlaceholderText text="Join Our Team - We're Hiring!" />
						</div>
						<div className="text-grey-600 text-sm">
							<PlaceholderText text="Discover exciting career opportunities with us." />
						</div>
					</div>
					<div>
						<div className="text-grey-600 ring ring-grey-200 p-2.5 text-sm rounded whitespace-nowrap">
							<PlaceholderText text="Learn More" />
						</div>
					</div>
				</div>
			</motion.div>
		</div>
	)
}
